import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import { VALID_PHONE_REGEX } from 'src/lib/regex'
import { phoneSchema } from 'src/shared/schemas/forms/consumer-auth'
import { formatInternationalPhoneNumber } from 'src/shared/utils/format/format-intl-phone-number'

import BusinessFormFieldControlled from '../../components/business-form-field-controlled'
import BusinessFormTitle from '../../components/business-form-title'
import { useBusinessCreateContext } from '../../context/business-create-context'
import { useBusinessCreateStore } from '../../store/business-create-store'

const PhoneNumberSchema = z.object({
  phoneNumber: phoneSchema,
})
type PhoneNumberSchemaType = z.infer<typeof PhoneNumberSchema>

const trimPhoneNumber = (value: string): string => {
  return value.replace(/[^\d+]/g, '').slice(0, 12)
}

export default function BusinessCreatePhoneNumberStep() {
  const { setFormData, formData } = useBusinessCreateStore()
  const { handleNext, handleGoToStep, setFooterConfig } = useBusinessCreateContext()

  const { handleSubmit, control } = useForm<PhoneNumberSchemaType>({
    resolver: zodResolver(PhoneNumberSchema),
    defaultValues: {
      phoneNumber: !formData.consumerPhone
        ? undefined
        : formatInternationalPhoneNumber(trimPhoneNumber(formData.consumerPhone) ?? ''),
    },
  })

  const phoneNumberValue = useWatch({
    control,
    name: 'phoneNumber',
  })

  useEffect(() => {
    const isValidPhoneNumber = VALID_PHONE_REGEX.test(trimPhoneNumber(phoneNumberValue || '') || '')

    setFooterConfig({
      secondaryButton: isValidPhoneNumber
        ? {
            text: "That's all I have",
            onClick: () => {
              setFormData({ ...formData, consumerPhone: phoneNumberValue })
              handleGoToStep('workflows')
            },
          }
        : undefined,
    })
  }, [handleGoToStep, phoneNumberValue, setFooterConfig, formData, setFormData])

  const onSubmit = (data: PhoneNumberSchemaType) => {
    // TODO: Verify the phone number is unformatted before storing it to store.
    setFormData({ ...formData, consumerPhone: data.phoneNumber })
    handleNext()
  }

  const formatPhoneNumberHelper = (value: string) => {
    const formatted = formatInternationalPhoneNumber(value)
    return formatted
  }

  return (
    <div className="flex size-full flex-col items-center justify-center gap-4">
      <BusinessFormTitle>{`What is ${formData.consumerFirstName || 'your client'}'s phone number?`}</BusinessFormTitle>
      <form id="business-create-form" onSubmit={handleSubmit(onSubmit)}>
        <BusinessFormFieldControlled
          control={control}
          name="phoneNumber"
          placeholder="Phone Number"
          type="tel"
          format={formatPhoneNumberHelper}
          autoFocus={true}
        />
      </form>
    </div>
  )
}
